/* globals npsConfig */

// Vanilla-JS implementation of the NPS feedback modal (previously jQuery-based).
// Behaviour is intended to be identical to the jQuery version.
(function () {
  const gaLabel = window.location.href;
  let userRate = 0;
  let continueId = null;
  // Source attribution for tracking; set from the trigger on each open.
  let caller = 'sticky';
  // Guards against re-entrant submits while the two-phase POST is in flight.
  let submitting = false;
  const dataLayer = typeof window.dataLayer === 'undefined' ? '' : window.dataLayer;

  // Default scroll depth (fraction of the page) that triggers the NPS modal on a
  // comparison page. Editors can override it per page via the
  // `nps_scroll_trigger_ratio` custom field (0–1), injected by the server as
  // `npsConfig.scrollTriggerRatio`.
  const NPS_SCROLL_TRIGGER_RATIO_DEFAULT = 0.3;

  // Per-browser cooldown (localStorage, shared across tabs). Not per-user:
  // resets on another browser/device, private mode, or cleared site data.
  // Cross-tab cap is best-effort (read/write isn't atomic).
  const NPS_SHOWN_KEY = 'npsModalShownAt';
  const NPS_COOLDOWN_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
  // Caps within a page load if localStorage fails.
  let shownThisPageLoad = false;

  // A show in another tab suppresses this one.
  window.addEventListener('storage', function (event) {
    if (event.key === NPS_SHOWN_KEY && event.newValue) {
      shownThisPageLoad = true;
    }
  });

  /** True if the modal was already shown this page load or within the cooldown. */
  function npsRecentlyShown() {
    if (shownThisPageLoad) {
      return true;
    }

    try {
      const last = Number(window.localStorage.getItem(NPS_SHOWN_KEY));
      // elapsed >= 0 guards against a future timestamp (clock skew).
      const elapsed = Date.now() - last;
      return isFinite(last) && elapsed >= 0 && elapsed < NPS_COOLDOWN_MS;
    } catch (e) {
      return false;
    }
  }

  /** Mark the modal shown: in-memory flag always, persisted cooldown if storage allows. */
  function markNpsShown() {
    shownThisPageLoad = true;
    try {
      window.localStorage.setItem(NPS_SHOWN_KEY, String(Date.now()));
    } catch (e) {
      /* storage blocked — shownThisPageLoad still caps this load */
    }
  }

  const shareButtons = document.querySelector('.share-buttons');
  const feedbackModal = document.querySelector('.nps-feedbackModal');
  const wufooBox = document.querySelector('#wufoo-feedback-box');

  (function initialize() {
    registerEvents();
    initNpsScrollTrigger();

    if (shareButtons && getComputedStyle(shareButtons).display !== 'none') {
      document.querySelectorAll('.nps-feedbackModal__teaser').forEach(function (teaser) {
        teaser.classList.add('nps-feedbackModal__teaser--alignToTopOfShareBtns');
      });
    }
  })();

  /**
   * Attach a delegated listener on `root` that fires `handler(event, matchedEl)`
   * when the event originates inside an element matching `selector` — the
   * vanilla equivalent of jQuery's `$(root).on(type, selector, handler)`.
   */
  function delegate(root, type, selector, handler) {
    if (!root) {
      return;
    }

    root.addEventListener(type, function (event) {
      const match = event.target.closest(selector);
      if (match && root.contains(match)) {
        handler(event, match);
      }
    });
  }

  function registerEvents() {
    delegate(
      document.body,
      'click',
      '.nps-feedbackModal__teaser, #wufoo-feedback-box button',
      function (event, el) {
        openNpsFeedbackModal(el);
      },
    );

    delegate(feedbackModal, 'click', '.modal-box__overlay', closeNpsFeedbackModal);
    delegate(feedbackModal, 'click', '.nps-feedbackModal__cancel', closeNpsFeedbackModal);
    delegate(feedbackModal, 'click', '.nps-feedbackModal__done', closeNpsFeedbackModal);
    delegate(feedbackModal, 'submit', '.nps-feedbackModal__form', handleSubmit);
    delegate(wufooBox, 'click', '.luna-button', switchMessage);

    document.querySelectorAll('.nps-feedbackModal__singleTab').forEach(function (tab) {
      tab.addEventListener('click', userRating);
    });
  }

  function switchMessage() {
    document.querySelectorAll('.wufoo-feedback-start').forEach(function (el) {
      el.classList.add('is-hidden');
    });

    const wufooFeedback = document.querySelector('.wufoo-feedback');
    if (wufooFeedback) {
      wufooFeedback.setAttribute('aria-describedby', 'feedback-end');
    }

    document.querySelectorAll('.wufoo-feedback-end').forEach(function (el) {
      el.classList.remove('is-hidden');
    });
  }

  function openNpsFeedbackModal(trigger) {
    if (!feedbackModal) {
      return;
    }

    // Restore the form view: the modal can be reopened in the same session
    // (e.g. via the teaser) after a previous submit left the thank-you panel showing.
    resetModalView();
    feedbackModal.setAttribute('aria-hidden', 'false');

    // Attribute the source on every open: 'in context' ("Was this useful") when
    // opened from the in-content wufoo box, otherwise the sidebar teaser or the
    // scroll/GTM trigger ('Sidebar'). Set fresh each time so a stale value from
    // an earlier open can't mis-attribute a later submission.
    caller = trigger && wufooBox && wufooBox.contains(trigger) ? 'in context' : 'sticky';
  }

  /**
   * Reset the modal to its initial (form-visible) state: show the form, hide the
   * thank-you panel, clear the in-flight guard and re-enable Submit.
   */
  function resetModalView() {
    submitting = false;

    const form = document.querySelector('.nps-feedbackModal__form');
    if (form) {
      form.classList.remove('is-hidden');
    }

    const thanks = document.querySelector('.nps-feedbackModal__thanks');
    if (thanks) {
      thanks.classList.add('is-hidden');
    }

    const submit = document.querySelector('.nps-feedbackModal__form [type="submit"]');
    if (submit) {
      submit.disabled = false;
    }
  }

  function closeNpsFeedbackModal() {
    if (feedbackModal) {
      feedbackModal.setAttribute('aria-hidden', 'true');
    }
  }

  function handleSubmit(event) {
    event.preventDefault();

    // Ignore re-entrant submits (double-click / Enter) while a POST is in flight,
    // otherwise each fires a fresh score POST and creates duplicate NPS records.
    if (submitting) {
      return;
    }

    const errors = validateNps();
    showError(errors);

    if (errors.length > 0) {
      return;
    }

    submitting = true;
    const submit = document.querySelector('.nps-feedbackModal__form [type="submit"]');
    if (submit) {
      submit.disabled = true;
    }

    trackNpsFormSubmit(caller);

    // The tracking API is two-phase: POST the score first, then POST the written
    // feedback against the `continue` id the score call returns. If the score
    // POST fails — or succeeds without returning a `continue` id — there's
    // nothing to post the feedback against, so finish at the thank-you state
    // rather than leaving the modal hanging (NPS tracking is best-effort).
    const finish = function () {
      submitting = false;
      showThanks();
    };

    saveData(
      null,
      userRate,
      function () {
        if (continueId) {
          saveData(continueId, null, finish);
        } else {
          finish();
        }
      },
      finish,
    );
  }

  function validateNps() {
    const errors = [];

    if (!document.querySelector('.nps-feedbackModal__singleTab--active')) {
      errors.push('Rating');
    }

    const select = document.querySelector('.nps-feedbackModal .luna-select');
    if (select && select.value === 'none') {
      errors.push('Select');
    }

    const textarea = document.querySelector('.nps-feedbackModal .luna-input--textarea');
    if (textarea && textarea.value.trim() === '') {
      errors.push('Textarea');
    }

    return errors;
  }

  function showError(errorKeys) {
    document.querySelectorAll('.nps-feedbackModal__error').forEach(function (el) {
      el.classList.add('is-hidden');
      if (el.parentElement) {
        el.parentElement.classList.remove('is-invalid');
      }
    });

    errorKeys.forEach(function (key) {
      document.querySelectorAll('.nps-feedbackModal__error' + key).forEach(function (el) {
        el.classList.remove('is-hidden');
        if (el.parentElement) {
          el.parentElement.classList.add('is-invalid');
        }
      });
    });
  }

  function showThanks() {
    const form = document.querySelector('.nps-feedbackModal__form');
    if (form) {
      form.classList.add('is-hidden');
    }

    const thanks = document.querySelector('.nps-feedbackModal__thanks');
    if (thanks) {
      thanks.classList.remove('is-hidden');
    }
  }

  /**
   * Track the NPS form submit both via:
   * - global Finder tracking component
   * - ga4 tracking
   *
   * @param caller string
   */
  function trackNpsFormSubmit(caller) {
    const {
      nps_feedback_form: {
        elements: { comment, category },
      },
    } = document.forms;

    if (!comment || !category) {
      return;
    }

    const categoryName = category.options[category.selectedIndex].textContent.trim() || '',
      score = parseInt(userRate, 10),
      customEventParams = {
        source: caller === 'in context' ? 'Was this useful' : 'Sidebar',
        feedback: comment.value || '',
        score,
        categoryName,
      };

    window.finder.tracking.track('npsSubmitted', {
      category: 'nps',
      action: 'submitted',
      label: categoryName,
      value: score,
      ...customEventParams,
    });

    document.dispatchEvent(
      new CustomEvent(`ga4.track`, {
        detail: {
          name: 'npsSubmitted',
          params: customEventParams,
        },
      }),
    );
  }

  function userRating() {
    const active = document.querySelector('.nps-feedbackModal__singleTab--active');
    if (active) {
      active.classList.remove('nps-feedbackModal__singleTab--active');
    }

    this.classList.add('nps-feedbackModal__singleTab--active');
    userRate = this.textContent.trim();
  }

  function getDeviceType() {
    if (window.matchMedia('(any-hover:none)').matches) {
      if (window.matchMedia('(max-width: 915px)').matches) {
        return 'mobile';
      }
      return 'tablet';
    }
    return 'desktop';
  }

  /**
   * Serialise the tracking payload as application/x-www-form-urlencoded with
   * bracket notation (`data[key]=value`) — matching how jQuery's `$.ajax`
   * serialised `{ data: {...} }`, which the tracking API expects.
   *
   * @param {Object} fields
   * @returns {string}
   */
  function encodeTrackingBody(fields) {
    const params = new URLSearchParams();
    Object.keys(fields).forEach(function (key) {
      const value = fields[key];
      params.append('data[' + key + ']', value == null ? '' : String(value));
    });
    return params.toString();
  }

  function saveData(id, rate, onComplete, onError) {
    const selectEl = document.querySelector('.nps-feedbackModal .luna-select');
    const textareaEl = document.querySelector('.nps-feedbackModal .luna-input--textarea');
    const feedbackTitle = selectEl ? selectEl.value : '';
    const feedbackContent = textareaEl ? textareaEl.value : '';
    // `dataLayer` is '' when window.dataLayer is absent — guard so a missing/empty
    // layer yields null rather than throwing synchronously before the request.
    const nicheName = (dataLayer && dataLayer[0] && dataLayer[0].category) || null;
    const host = window.location.hostname;
    const apiUrl = `https://${getAuUrl(host)}`;
    const deviceType = getDeviceType();
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' };

    if (!id) {
      // Phase 1 — score. Mirrors jQuery `success`/`error`: continue only on a
      // 2xx JSON response, otherwise run the error path.
      fetch(`${apiUrl}/api/v1.2/?name=tracking`, {
        method: 'POST',
        headers: headers,
        body: encodeTrackingBody({
          site: npsConfig.siteUrl,
          url: gaLabel,
          gaid: npsConfig.gaUserId,
          score: rate,
          niche: nicheName,
          device: deviceType,
        }),
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('NPS score POST failed: ' + response.status);
          }
          return response.json();
        })
        .then(function (result) {
          continueId = result.continue;
          if (typeof onComplete === 'function') {
            onComplete();
          }
        })
        .catch(function () {
          if (typeof onError === 'function') {
            onError();
          }
        });
    } else {
      // Phase 2 — written feedback. Mirrors jQuery `complete`: the callback runs
      // regardless of success or failure.
      const done = function () {
        if (typeof onComplete === 'function') {
          onComplete();
        }
      };

      fetch(`${apiUrl}/api/v1.2/?name=tracking&continue=` + id, {
        method: 'POST',
        headers: headers,
        body: encodeTrackingBody({
          feedbackTitle: feedbackTitle,
          feedback: feedbackContent,
          device: deviceType,
        }),
      }).then(done, done);
    }
  }

  /**
   * If the current host URL is a FIN URL, return the corresponding
   * FUS host URL
   *
   * @param {string} currentHostUrl
   * @returns {string}
   */
  function getAuUrl(currentHostUrl) {
    switch (currentHostUrl) {
      case 'www.finder.com':
        return 'www.finder.com.au';
      case 'fus.dev':
        return 'fin.dev';
      case 'site-fus.qa01.au-se1.gcp.finder.com':
        return 'site-fin.qa01.au-se1.gcp.finder.com';
      case 'site-fus.qa02.au-se1.gcp.finder.com':
        return 'site-fin.qa02.au-se1.gcp.finder.com';
      case 'site-fus.qa03.au-se1.gcp.finder.com':
        return 'site-fin.qa03.au-se1.gcp.finder.com';
      case 'site-fus.qa04.au-se1.gcp.finder.com':
        return 'site-fin.qa04.au-se1.gcp.finder.com';
      case 'site-fus.qa05.au-se1.gcp.finder.com':
        return 'site-fin.qa05.au-se1.gcp.finder.com';
      case 'site-fus.qa06.au-se1.gcp.finder.com':
        return 'site-fin.qa06.au-se1.gcp.finder.com';
      case 'fus.staging-01.finder.tools':
        return 'www.staging-06.finder.tools';
      case 'fus.staging-02.finder.tools':
        return 'www.staging-06.finder.tools';
      case 'fus.staging-04.finder.tools':
        return 'www.staging-06.finder.tools';
      case 'fus.staging-05.finder.tools':
        return 'www.staging-06.finder.tools';
      case 'fus.staging-06.finder.tools':
        return 'www.staging-06.finder.tools';
      case 'fus.staging-07.finder.tools':
        return 'www.staging-07.finder.tools';
      default:
        break;
    }

    return currentHostUrl;
  }

  /**
   * True when the current page renders a comparison table.
   *
   * `.js-comparison-calculator` is the outermost wrapper emitted for every
   * rendered `[table]` shortcode, and is the same signal used elsewhere in the
   * theme (chatbot comparisonTable parser, masthead client) to detect tables.
   *
   * @returns {boolean}
   */
  function isComparisonPage() {
    return document.querySelector('.js-comparison-calculator') !== null;
  }

  /**
   * Resolve the configured scroll-trigger ratio, falling back to the default for
   * a missing/invalid/out-of-range value so a bad config can't break or disable
   * the trigger.
   *
   * @returns {number}
   */
  function getScrollTriggerRatio() {
    const configured = typeof npsConfig !== 'undefined' ? Number(npsConfig.scrollTriggerRatio) : NaN;

    if (!isFinite(configured) || configured <= 0 || configured > 1) {
      return NPS_SCROLL_TRIGGER_RATIO_DEFAULT;
    }

    return configured;
  }

  /**
   * Show the NPS modal once the user has scrolled past the configured depth on a
   * comparison page.
   *
   * Dispatches the existing `showNpsModal` event so the cooldown and
   * rewards-table checks in its handler still apply.
   */
  function initNpsScrollTrigger() {
    if (!isComparisonPage() || npsRecentlyShown()) {
      return;
    }

    const ratio = getScrollTriggerRatio();
    let fired = false;

    function onScroll() {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;

      if (scrollable <= 0) {
        return;
      }

      if (!fired && doc.scrollTop / scrollable >= ratio) {
        fired = true;
        window.removeEventListener('scroll', onScroll);
        window.dispatchEvent(new Event('showNpsModal'));
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  window.addEventListener('showNpsModal', function () {
    if (npsRecentlyShown()) {
      return;
    }

    const hasRewardsInTable =
      (window.rewardsConfigs && Object.keys(window.rewardsConfigs).length > 0) ||
      document.querySelector('.rewards--element-container') !== null;

    if (hasRewardsInTable) {
      return;
    }

    // Don't start the cooldown if there's no modal to show (in-content-only page).
    if (!feedbackModal) {
      return;
    }

    markNpsShown();
    openNpsFeedbackModal();
  });
})();
